import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Lock, Send, UserCheck } from "lucide-react";
import toast from "react-hot-toast";

import { groupChatMessages } from "@packages/shared-utils";

import { PageHeader } from "@/components/shared/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { useSupportSocket } from "@/hooks/useSupportSocket";
import { SupportContextPanel } from "@/pages/platform-support/SupportContextPanel";
import {
  assignSupportConversationToMe,
  getAgentSupportMessages,
  markAgentSupportConversationRead,
  getSupportInbox,
  postAgentSupportMessage,
  setSupportConversationStatus,
  type SupportConversationStatus,
  type SupportMessage,
} from "@/services/supportChatService";

/** The raw enum leaked to screen as "open"; operators read Spanish. */
const STATUS_LABEL: Record<string, string> = {
  open: "Abierta",
  pending: "Pendiente",
  snoozed: "Pospuesta",
  closed: "Cerrada",
};

const STATUS_FILTERS: Array<{
  value: SupportConversationStatus | "all";
  label: string;
}> = [
  { value: "all", label: "Activas" },
  { value: "open", label: "Abiertas" },
  { value: "pending", label: "Pendientes" },
  { value: "snoozed", label: "Pospuestas" },
  { value: "closed", label: "Cerradas" },
];

/** "43 min", "2 h", "3 d" — how long this customer has been waiting. */
function formatWaiting(ms: number): string {
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h`;
  return `${Math.floor(hours / 24)} d`;
}

/**
 * A thread nobody has answered for over an hour is the failure this
 * feature exists to prevent, so the inbox says so in red rather than
 * making the operator subtract timestamps.
 */
function waitingTone(ms: number): string {
  if (ms > 60 * 60_000) return "text-destructive";
  if (ms > 15 * 60_000) return "text-foreground";
  return "text-muted-foreground";
}

/**
 * The face beside an incoming bubble.
 *
 * On this screen "incoming" is the CUSTOMER, so this is where an operator
 * sees who they are answering. A visitor has no account and so no image —
 * they get their initial, which still beats four identical grey bubbles
 * when several threads are open side by side.
 */
function MessageAvatar({
  authorName,
  authorAvatar,
}: {
  authorName: string | null;
  authorAvatar: string | null;
}): JSX.Element {
  if (authorAvatar) {
    return (
      <img
        src={authorAvatar}
        alt=""
        aria-hidden="true"
        className="h-7 w-7 shrink-0 rounded-full object-cover"
      />
    );
  }
  return (
    <span
      aria-hidden="true"
      className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted text-[11px] font-semibold text-muted-foreground"
    >
      {(authorName ?? "?").charAt(0).toUpperCase()}
    </span>
  );
}

/** HH:MM — a thread without times reads as a log, not a conversation. */
function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString("es-UY", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function mergeMessages(
  current: SupportMessage[],
  incoming: SupportMessage[],
): SupportMessage[] {
  const byId = new Map(current.map((m) => [m.id, m]));
  for (const message of incoming) byId.set(message.id, message);
  return [...byId.values()].sort((a, b) =>
    a.createdAt.localeCompare(b.createdAt),
  );
}

export function PlatformSupportPage(): JSX.Element {
  const [statusFilter, setStatusFilter] = useState<
    SupportConversationStatus | "all"
  >("all");
  /**
   * Preselect from ?conversationId=.
   *
   * Every operator alert — push deep link and email link alike — points
   * here with the conversation id, and nothing read it: the operator
   * followed "Responder" and landed on "Elegí una conversación", with the
   * thread they were alerted about nowhere in sight. Read once, as the
   * initial state, so a later manual selection is not fought by the URL.
   */
  const [selectedId, setSelectedId] = useState<string | null>(() =>
    new URLSearchParams(window.location.search).get("conversationId"),
  );
  const [messages, setMessages] = useState<SupportMessage[]>([]);
  const [draft, setDraft] = useState("");
  const [internalNote, setInternalNote] = useState(false);
  const [sending, setSending] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [customerTyping, setCustomerTyping] = useState(false);
  const [customerLastReadAt, setCustomerLastReadAt] = useState<string | null>(
    null,
  );
  const typingTimer = useRef<number | undefined>(undefined);

  const inbox = useQuery({
    queryKey: ["platform-support-inbox", statusFilter],
    queryFn: () =>
      getSupportInbox(statusFilter === "all" ? undefined : statusFilter),
    refetchInterval: 30_000,
  });

  /** The selected row, for the take-over control's state. */
  const selectedConversation = inbox.data?.find((c) => c.id === selectedId);

  /**
   * The last thing WE sent that the customer has read. Null for a visitor
   * thread: an anonymous visitor has no participant row, so there is no
   * read state to report and we must not imply one.
   */
  const lastReadOurMessageId = ((): string | null => {
    if (!customerLastReadAt) return null;
    const readUpTo = new Date(customerLastReadAt).getTime();
    const ours = messages.filter(
      (m) =>
        (m.authorKind === "agent" || m.authorKind === "ai") &&
        new Date(m.createdAt).getTime() <= readUpTo,
    );
    // A real check, not a non-null assertion: the filter above can leave
    // the array empty and the type checker is right to say so. (No
    // .at(-1) — the portal's lib target predates it.)
    const newest = ours[ours.length - 1];
    return newest ? newest.id : null;
  })();

  const refetchThread = useCallback(async (id: string): Promise<void> => {
    const thread = await getAgentSupportMessages(id);
    setMessages((current) => mergeMessages(current, thread.messages));
    setCustomerLastReadAt(thread.customerLastReadAt);
  }, []);

  useEffect(() => {
    if (!selectedId) {
      setMessages([]);
      return;
    }
    setMessages([]);
    void refetchThread(selectedId);
    // Tell the customer their message was seen. Opening the thread IS
    // reading it — waiting for a reply to stamp it would leave "Visto"
    // meaning "answered", which the reply already says.
    void markAgentSupportConversationRead(selectedId).catch(() => undefined);
  }, [selectedId, refetchThread]);

  const { sendTyping } = useSupportSocket(selectedId, {
    onMessage: (message) => {
      setMessages((current) => mergeMessages(current, [message]));
      // A message arriving ends the typing state it was preceded by.
      setCustomerTyping(false);
    },
    onResync: () => {
      if (selectedId) void refetchThread(selectedId);
    },
    onTyping: (side) => {
      if (side !== "customer") return;
      setCustomerTyping(true);
      window.clearTimeout(typingTimer.current);
      // Mirrors the server's 6s TTL: without this the indicator would
      // stay on forever once shown, promising a message nobody is writing.
      typingTimer.current = window.setTimeout(
        () => setCustomerTyping(false),
        6_000,
      );
    },
  });

  /** Position captured while scrolling — see SupportChatPanel for why. */
  const wasAtBottom = useRef(true);
  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    wasAtBottom.current =
      el.scrollHeight - el.scrollTop - el.clientHeight < 120;
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !wasAtBottom.current) return;
    el.scrollTo({ top: el.scrollHeight });
  }, [messages]);

  const send = async (): Promise<void> => {
    const trimmed = draft.trim();
    if (!trimmed || !selectedId || sending) return;
    setSending(true);
    try {
      const message = await postAgentSupportMessage(
        selectedId,
        trimmed,
        internalNote,
      );
      setMessages((current) => mergeMessages(current, [message]));
      setDraft("");
      setInternalNote(false);
      void inbox.refetch();
    } catch {
      toast.error("No pudimos enviar el mensaje");
    } finally {
      setSending(false);
    }
  };

  /**
   * Take the thread. Refetches the thread as well as the inbox because
   * taking it now POSTS a handover line the customer sees — the operator
   * should be looking at the same thread the customer is.
   */
  const takeConversation = async (): Promise<void> => {
    if (!selectedId) return;
    try {
      await assignSupportConversationToMe(selectedId);
      await refetchThread(selectedId);
      void inbox.refetch();
    } catch {
      toast.error("No pudimos tomar la conversación");
    }
  };

  const changeStatus = async (
    status: SupportConversationStatus,
  ): Promise<void> => {
    if (!selectedId) return;
    try {
      await setSupportConversationStatus(selectedId, status);
      void inbox.refetch();
    } catch {
      toast.error("No pudimos cambiar el estado");
    }
  };

  return (
    <div className="fx-content space-y-6">
      <PageHeader
        title="Soporte"
        description="Conversaciones con clientes y visitantes. La más esperada, primero."
      />

      <div className="flex flex-wrap gap-2">
        {STATUS_FILTERS.map((filter) => (
          <Button
            key={filter.value}
            variant={statusFilter === filter.value ? "default" : "outline"}
            size="sm"
            onClick={() => setStatusFilter(filter.value)}
          >
            {filter.label}
          </Button>
        ))}
      </div>

      {/*
        minmax(0,…) on every track: fixed rem columns plus the app sidebar
        overflowed the viewport and pushed the context panel off-screen.
        The context column drops below the thread under 1280px rather than
        squeezing all three into a width none of them can use.
      */}
      <div className="grid gap-4 xl:grid-cols-[minmax(0,18rem)_minmax(0,1fr)_minmax(0,17rem)]">
        {/* Inbox ----------------------------------------------------- */}
        <div className="max-h-[calc(100vh-17rem)] min-h-[20rem] overflow-y-auto rounded-lg border border-border bg-card">
          {inbox.isLoading ? (
            <div className="flex h-32 items-center justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : (inbox.data?.length ?? 0) === 0 ? (
            <p className="p-6 text-center text-sm text-muted-foreground">
              No hay conversaciones.
            </p>
          ) : (
            <ul className="divide-y divide-border">
              {inbox.data?.map((conversation) => (
                <li key={conversation.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedId(conversation.id)}
                    className={[
                      // border-l on every row, transparent until selected:
                      // colouring it only when active would shift the text
                      // 3px sideways on click, which reads as a misclick.
                      "w-full cursor-pointer border-l-[3px] px-4 py-3 text-left transition-all duration-150 hover:bg-muted",
                      selectedId === conversation.id
                        ? "border-l-accent bg-muted"
                        : "border-l-transparent",
                    ].join(" ")}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate text-sm font-medium text-foreground">
                        {conversation.subject ?? conversation.label}
                      </span>
                      {/*
                        An accountant asks about OTHER companies' books, so
                        the fiscal panel beside their thread describes a
                        client they happen to be a member of, not them.
                        Worth knowing before you open it, not after.
                      */}
                      {conversation.audience === "accountant" ? (
                        <Badge variant="secondary" className="shrink-0">
                          Contador
                        </Badge>
                      ) : null}
                      {/*
                        A thread whose last word is a system marker and
                        that nobody has taken is one that ASKED for a
                        person. Without this it reads "Respondida" in the
                        list — the AI did answer — so the one row that
                        actually needs a human looks like the one that
                        does not. Derived from the preview rather than a
                        new column: it stops being true the moment someone
                        takes it, which is exactly when it should.
                      */}
                      {conversation.previewAuthorKind === "system" &&
                      !conversation.assigneeUserId ? (
                        <Badge className="fx-chat-badge shrink-0">
                          Pidió una persona
                        </Badge>
                      ) : null}
                      <Badge variant="outline">
                        {STATUS_LABEL[conversation.status] ??
                          conversation.status}
                      </Badge>
                    </div>
                    {conversation.preview ? (
                      <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">
                        {conversation.previewAuthorKind === "agent" ||
                        conversation.previewAuthorKind === "ai"
                          ? "Vos: "
                          : ""}
                        {conversation.preview}
                      </p>
                    ) : null}
                    <p
                      className={[
                        "mt-1 text-[11px] font-medium",
                        conversation.waitingSinceMs === null
                          ? "text-muted-foreground"
                          : waitingTone(conversation.waitingSinceMs),
                      ].join(" ")}
                    >
                      {conversation.waitingSinceMs === null
                        ? "Respondida"
                        : `Esperando hace ${formatWaiting(conversation.waitingSinceMs)}`}
                    </p>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Thread ---------------------------------------------------- */}
        <div className="flex h-[calc(100vh-17rem)] min-h-[20rem] min-w-0 flex-col rounded-lg border border-border bg-card">
          {!selectedId ? (
            <p className="flex flex-1 items-center justify-center text-sm text-muted-foreground">
              Elegí una conversación.
            </p>
          ) : (
            <>
              <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-2">
                <div className="flex items-center gap-2">
                  {/*
                    The primary action on this screen, styled like it.
                    As an outline button called "Tomar", sitting between
                    "Pendiente" and "Cerrar", it read as a third status
                    toggle — an operator who had just been emailed "piden
                    hablar con una persona" could not find how to take the
                    conversation and assumed they should just start
                    typing. Typing does work, but it leaves the customer
                    with no idea who they are talking to.
                  */}
                  {selectedConversation?.assigneeUserId ? (
                    <span className="fx-chat-fade inline-flex items-center gap-1.5 rounded-full bg-muted px-2.5 py-1 text-xs text-muted-foreground">
                      <UserCheck className="h-3.5 w-3.5" />
                      Atendida — el cliente ya sabe quién está
                    </span>
                  ) : (
                    <Button
                      size="sm"
                      className="fx-chat-press"
                      onClick={() => void takeConversation()}
                    >
                      <UserCheck className="mr-1.5 h-4 w-4" />
                      Atender esta conversación
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => void changeStatus("pending")}
                  >
                    Pendiente
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => void changeStatus("closed")}
                  >
                    Cerrar
                  </Button>
                </div>
              </div>

              <div
                ref={scrollRef}
                onScroll={handleScroll}
                className="flex-1 space-y-3 overflow-y-auto px-4 py-3"
              >
                {groupChatMessages(messages).map(
                  ({ message, startsGroup, endsGroup, daySeparator }) => {
                    const dayChip = daySeparator ? (
                      <p className="py-1 text-center text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                        {daySeparator}
                      </p>
                    ) : null;
                    // "El cliente pidió hablar con una persona" is the
                    // thing an operator most needs to SEE here, and as a
                    // right-aligned bubble it read as something we said.
                    if (message.authorKind === "system") {
                      return (
                        <Fragment key={message.id}>
                          {dayChip}
                          <p className="fx-chat-fade px-4 text-center text-[11px] leading-relaxed text-muted-foreground">
                            {message.body}
                          </p>
                        </Fragment>
                      );
                    }
                    const fromUs = message.authorKind !== "customer";
                    const isNote = message.authorKind === "agent_private";
                    const isAi = message.authorKind === "ai";
                    return (
                      <Fragment key={message.id}>
                        {dayChip}
                        <div
                          className={[
                            "fx-chat-message flex items-end gap-2",
                            fromUs ? "justify-end" : "justify-start",
                            startsGroup ? "" : "-mt-2",
                          ].join(" ")}
                        >
                          {!fromUs ? (
                            startsGroup ? (
                              <MessageAvatar
                                authorName={message.authorName}
                                authorAvatar={message.authorAvatar}
                              />
                            ) : (
                              <span
                                aria-hidden="true"
                                className="h-7 w-7 shrink-0"
                              />
                            )
                          ) : null}
                          <div
                            className={[
                              "max-w-[75%] rounded-lg px-3 py-2 text-sm",
                              isNote
                                ? "border border-dashed border-border bg-muted/50 text-muted-foreground"
                                : isAi
                                  ? // The bot and a colleague were the same
                                    // teal bubble, so an operator scanning a
                                    // thread could not tell what the customer
                                    // had already been told by a machine from
                                    // what a person had promised them.
                                    "border border-accent/40 bg-accent/10 text-foreground"
                                  : fromUs
                                    ? "bg-accent text-accent-foreground"
                                    : "bg-muted text-foreground",
                            ].join(" ")}
                          >
                            {isNote ? (
                              <p className="mb-1 flex items-center gap-1 text-[10px] uppercase tracking-wide">
                                <Lock className="h-3 w-3" /> Nota interna
                              </p>
                            ) : startsGroup ? (
                              <span
                                className={[
                                  "mb-1 block text-[11px] font-semibold",
                                  fromUs && !isAi
                                    ? "text-accent-foreground/70"
                                    : "text-muted-foreground",
                                ].join(" ")}
                              >
                                {isAi
                                  ? "Asistente"
                                  : message.authorKind === "customer"
                                    ? "Cliente"
                                    : (message.authorName ?? "Equipo")}
                              </span>
                            ) : null}
                            <p className="whitespace-pre-wrap break-words">
                              {message.body}
                            </p>
                            {endsGroup ? (
                              <span
                                className={[
                                  "mt-1 block text-[10px]",
                                  fromUs && !isAi && !isNote
                                    ? "text-accent-foreground/60"
                                    : "text-muted-foreground/70",
                                ].join(" ")}
                              >
                                {formatTime(message.createdAt)}
                                {message.id === lastReadOurMessageId
                                  ? " · Visto por el cliente"
                                  : ""}
                              </span>
                            ) : null}
                          </div>
                        </div>
                      </Fragment>
                    );
                  },
                )}
                {customerTyping ? (
                  <div className="flex justify-start">
                    <div
                      className="flex items-center gap-1 rounded-lg bg-muted px-3 py-2.5"
                      aria-label="El cliente está escribiendo"
                    >
                      <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-muted-foreground [animation-delay:-0.3s]" />
                      <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-muted-foreground [animation-delay:-0.15s]" />
                      <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-muted-foreground" />
                    </div>
                  </div>
                ) : null}
              </div>

              <div className="border-t border-border p-3">
                <div className="mb-2 flex items-center gap-2">
                  <Label
                    htmlFor="support-internal-note"
                    className="flex cursor-pointer items-center gap-2 text-xs font-normal text-muted-foreground"
                  >
                    <Checkbox
                      id="support-internal-note"
                      checked={internalNote}
                      onCheckedChange={(checked) => setInternalNote(checked === true)}
                    />
                    Nota interna (el cliente no la ve)
                  </Label>
                </div>
                <div className="flex items-end gap-2">
                  <Textarea
                    value={draft}
                    onChange={(e) => {
                      setDraft(e.target.value);
                      sendTyping();
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        void send();
                      }
                    }}
                    rows={3}
                    placeholder={
                      internalNote
                        ? "Nota para el equipo…"
                        : "Respuesta al cliente…"
                    }
                    className="resize-none"
                  />
                  <Button
                    type="button"
                    size="icon"
                    onClick={() => void send()}
                    disabled={sending || draft.trim().length === 0}
                    aria-label="Enviar"
                  >
                    {sending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Context --------------------------------------------------- */}
        <SupportContextPanel
          conversationId={selectedId}
          onUseReply={(body) => {
            // Append rather than replace: an operator who already typed
            // something should not lose it to a stray click.
            setDraft((current) =>
              current.trim() ? `${current.trim()}\n\n${body}` : body,
            );
            setInternalNote(false);
          }}
        />
      </div>
    </div>
  );
}
